import React, { useMemo, useRef, useState, useEffect, RefObject } from "react";
import { useAppStore } from "@/stores/appStore";
import { useFieldConfig } from "@/hooks/useFieldConfig";
import { useLabsFeatures } from "@/hooks/useLabsFeatures";
import { StatusDot } from "@/components/ui/Badge";
import type { Initiative, SubProduct } from "@/types";
import { LinkIcon, ChatBubbleLeftIcon, ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { ICON_MAP } from "@/components/modals/IconPicker";
import { formatMonthLabel } from "@/utils/monthLabel";
import { MonthLabel } from "@/components/ui/MonthLabel";
import { isDateMode } from "@/components/timeline/TimelineWeekView";
import { formatActualDate } from "@/utils/dateRange";
import { INITIATIVE_TITLE_LINK_CLASS } from "@/utils/uiConventions";
import { ClassificationFooter } from "@/components/ui/ClassificationFooter";

// Component for truncated title with tooltip (only shows when actually truncated).
// className/onClick apply the app-wide "clickable initiative title" convention — see the
// design note above this component's usage below for the canonical class string.
function TruncatedTitle({
  title,
  className,
  onClick,
}: {
  title: string;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
}) {
  const titleRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (titleRef.current) {
        setIsTruncated(titleRef.current.scrollWidth > titleRef.current.clientWidth);
      }
    };

    checkTruncation();
    const observer = new ResizeObserver(checkTruncation);
    if (titleRef.current) {
      observer.observe(titleRef.current);
    }
    return () => observer.disconnect();
  }, [title]);

  return (
    <div className="relative min-w-0 flex-1">
      <span
        ref={titleRef}
        className={`truncate block ${className || ''}`}
        onClick={onClick}
        onMouseEnter={() => isTruncated && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {title}
      </span>
      {showTooltip && (
        <div className="absolute top-full left-0 mt-1 px-2 py-1 text-xs bg-gray-900 text-white rounded z-50 max-w-md break-words">
          {title}
        </div>
      )}
    </div>
  );
}

// Renders up to 2 tags inline; any remaining collapse into a hover "+N" pill so a
// tag-heavy row can't widen the Tags column for every record.
function TagList({ tags }: { tags?: Initiative["tags"] }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const valid = (tags || []).filter((t) => t.tag);
  const MAX_VISIBLE = 2;
  const visible = valid.slice(0, MAX_VISIBLE);
  const hidden = valid.slice(MAX_VISIBLE);

  const renderTag = (initTag: NonNullable<Initiative["tags"]>[number]) => {
    const IconComponent = initTag.tag?.icon ? ICON_MAP[initTag.tag.icon] : null;
    return (
      <div key={initTag.tagId} className="flex items-center gap-1.5 text-xs text-text-secondary">
        {IconComponent && <IconComponent className="w-3.5 h-3.5" />}
        <span>{initTag.tag?.name}</span>
      </div>
    );
  };

  return (
    <div className="flex items-center gap-2">
      {visible.map(renderTag)}
      {hidden.length > 0 && (
        <div className="relative flex-shrink-0">
          <span
            className="text-xs text-text-secondary bg-gray-100 rounded-full px-1.5 py-0.5 cursor-default"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            +{hidden.length}
          </span>
          {showTooltip && (
            <div className="absolute top-full right-0 mt-1 px-2 py-1.5 bg-gray-900 text-white rounded z-50 flex flex-col gap-1.5 whitespace-nowrap">
              {hidden.map((initTag) => {
                const IconComponent = initTag.tag?.icon ? ICON_MAP[initTag.tag.icon] : null;
                return (
                  <div key={initTag.tagId} className="flex items-center gap-1.5 text-xs">
                    {IconComponent && <IconComponent className="w-3.5 h-3.5" />}
                    <span>{initTag.tag?.name}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const MONTH_TO_Q: Record<string, string> = {
  Jan: 'Q1', Feb: 'Q1', Mar: 'Q1',
  Apr: 'Q2', May: 'Q2', Jun: 'Q2',
  Jul: 'Q3', Aug: 'Q3', Sep: 'Q3',
  Oct: 'Q4', Nov: 'Q4', Dec: 'Q4',
};

function withQuarterGroup(fullLabel: string): string {
  const parts = fullLabel.split(' ');
  if (parts.length !== 2) return fullLabel;
  const q = MONTH_TO_Q[parts[1]];
  return q ? `${parts[0]} ${q} ${parts[1]}` : fullLabel;
}

interface ListViewProps {
  initiatives?: Initiative[];
  containerRef?: RefObject<HTMLDivElement>;
  viewMode?: 'weekly' | 'monthly' | 'quarterly';
}

export function ListView({ initiatives: filteredInitiatives, containerRef, viewMode = 'monthly' }: ListViewProps) {
  const { products, initiatives: allInitiatives, quarters, openViewInitiativeModal } = useAppStore();
  const fc = useFieldConfig();
  const labs = useLabsFeatures();

  // Use filtered initiatives if provided, otherwise use all
  const initiatives = filteredInitiatives || allInitiatives;

  const isWeekly = viewMode === 'weekly';
  // Weekly shows only date-mode initiatives (exact dates); month-mode rows are hidden.
  const displayInitiatives = useMemo(
    () => (isWeekly ? initiatives.filter(isDateMode) : initiatives),
    [initiatives, isWeekly],
  );
  const hiddenMonthModeCount = initiatives.length - displayInitiatives.length;

  // Sort direction for the Start column (default earliest-first)
  const [startSortDir, setStartSortDir] = useState<'asc' | 'desc'>('asc');

  // Chronological position of each quarter (by start date) so initiatives can be
  // ordered by which one starts first within a grouping.
  const quarterOrder = useMemo(() => {
    const m = new Map<string, number>();
    [...quarters]
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
      .forEach((q, i) => m.set(q.key, i));
    return m;
  }, [quarters]);

  const startVal = (init: Initiative) =>
    isWeekly
      ? (init.startDate ? new Date(init.startDate).getTime() : Number.MAX_SAFE_INTEGER)
      : (quarterOrder.get(init.startQuarter ?? '') ?? Number.MAX_SAFE_INTEGER);

  // Group initiatives by sub-product
  const initiativesBySubProduct = useMemo(() => {
    const grouped = new Map<string, Initiative[]>();
    displayInitiatives.forEach((init) => {
      const key = init.subProductId;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(init);
    });
    const dir = startSortDir === 'asc' ? 1 : -1;
    grouped.forEach((arr) =>
      arr.sort((a, b) => dir * (startVal(a) - startVal(b)) || (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()))
    );
    return grouped;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayInitiatives, quarterOrder, startSortDir, isWeekly]);

  // Group sub-products by product
  const subProductsByProduct = useMemo(() => {
    const grouped = new Map<string, SubProduct[]>();
    products.forEach((product) => {
      if (product.subProducts) {
        grouped.set(product.id, product.subProducts);
      }
    });
    return grouped;
  }, [products]);

  // Count initiatives for a product
  const getProductInitiativeCount = (productId: string): number => {
    const subProducts = subProductsByProduct.get(productId) || [];
    return subProducts.reduce((count, sp) => {
      return count + (initiativesBySubProduct.get(sp.id)?.length || 0);
    }, 0);
  };

  // Get quarter label from key — honours Monthly / Quarterly view mode
  const getQuarterLabel = (quarterKey: string) => {
    const quarter = quarters.find((q) => q.key === quarterKey);
    const label = quarter?.fullLabel || quarterKey;
    return formatMonthLabel(viewMode === 'quarterly' ? withQuarterGroup(label) : label);
  };

  // Dynamic colSpan based on visible columns: Name + Status + Start + (End?) + (Owner?) + (Tags?)
  const totalColumns = 3 + (fc.isVisible('end') ? 1 : 0) + (fc.isVisible('owner') ? 1 : 0) + (fc.isVisible('tags') ? 1 : 0);

  if (isWeekly && displayInitiatives.length === 0) {
    return (
      <div ref={containerRef} className="h-full flex flex-col items-center justify-center text-center px-6">
        <p className="text-text-secondary text-sm max-w-xl">
          Found {hiddenMonthModeCount} {hiddenMonthModeCount === 1 ? "record" : "records"} — switch to Monthly or Quarterly to see them.
          <br />
          Weekly shows only initiatives entered with exact dates (Date mode).
        </p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="h-full flex flex-col">
      {/* List View - Scrollable */}
      <div className="flex-1 overflow-auto scrollbar-thin">
        <table className="w-full border-collapse">
          {/* Table Header */}
          <thead className="sticky top-0 z-10 bg-background-secondary">
            <tr>
              <th className="text-left py-2 px-4 border-b border-gray-200 font-semibold text-text-primary text-sm">
                {fc.label('initiative')} Name
              </th>
              <th className="text-left py-2 px-4 border-b border-gray-200 font-semibold text-text-primary text-sm">
                {fc.label('status')}
              </th>
              <th className="text-left py-2 px-4 border-b border-gray-200 font-semibold text-sm">
                <button
                  type="button"
                  onClick={() => setStartSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                  aria-sort={startSortDir === 'asc' ? 'ascending' : 'descending'}
                  title="Sort by start"
                  className="inline-flex items-center gap-1 text-blue-600 transition-colors"
                >
                  {fc.label('start')}
                  {startSortDir === 'asc' ? (
                    <ChevronUpIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  ) : (
                    <ChevronDownIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  )}
                </button>
              </th>
              {fc.isVisible('end') && (
                <th className="text-left py-2 px-4 border-b border-gray-200 font-semibold text-text-primary text-sm">
                  {fc.label('end')}
                </th>
              )}
              {fc.isVisible('owner') && (
                <th className="text-left py-2 px-4 border-b border-gray-200 font-semibold text-text-primary text-sm">
                  {fc.label('owner')}
                </th>
              )}
              {fc.isVisible('tags') && (
                <th className="text-left py-2 px-4 border-b border-gray-200 font-semibold text-text-primary text-sm">
                  {fc.label('tags')}
                </th>
              )}
            </tr>
          </thead>

          {/* Table Body */}
          <tbody>
            {products.map((product) => {
              const subProducts = subProductsByProduct.get(product.id) || [];
              const productInitiativeCount = getProductInitiativeCount(product.id);

              // Hide products with no matching initiatives
              if (productInitiativeCount === 0) return null;

              return (
                <React.Fragment key={product.id}>
                  {/* Product Header Row - Light blue background */}
                  <tr className="bg-blue-50/50 border-b border-gray-100">
                    <td
                      colSpan={totalColumns}
                      className="py-2 px-4 font-semibold text-text-primary text-sm"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {product.icon && ICON_MAP[product.icon] && (
                          <span style={{ color: product.iconColor || '#6B7280' }}>
                            {(() => {
                              const IconComponent = ICON_MAP[product.icon];
                              return <IconComponent className="w-4 h-4 flex-shrink-0" />;
                            })()}
                          </span>
                        )}
                        <span className="truncate" title={product.name}>{product.name}</span>
                        <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full flex-shrink-0">
                          {productInitiativeCount}
                        </span>
                      </div>
                    </td>
                  </tr>

                  {/* Sub-products and Initiatives */}
                  {subProducts.map((subProduct) => {
                    const subProductInitiatives =
                      initiativesBySubProduct.get(subProduct.id) || [];

                    // Skip sub-products with no initiatives
                    if (subProductInitiatives.length === 0) return null;

                    return (
                      <React.Fragment key={subProduct.id}>
                        {/* Sub-product Header Row */}
                        <tr className="bg-surface-subtle border-b border-gray-100">
                          <td
                            colSpan={totalColumns}
                            className="py-1.5 px-3 pl-8 font-medium text-sm text-text-secondary"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="truncate" title={subProduct.name}>{subProduct.name}</span>
                              <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full flex-shrink-0">
                                {subProductInitiatives.length}
                              </span>
                            </div>
                          </td>
                        </tr>

                        {/* Initiatives under this sub-product */}
                        {subProductInitiatives.map((initiative) => {
                          const classification = initiative.classification;
                          const status = initiative.status;
                          const outgoingCount = initiative.dependencies?.length || 0;
                          const reverseCount = (initiative as any)._count?.dependents || 0;
                          const hasDependencies = outgoingCount > 0 || reverseCount > 0;
                          const dependencyTooltip =
                            outgoingCount > 0 && reverseCount > 0
                              ? `Depends on ${outgoingCount} · ${reverseCount} depend on this`
                              : outgoingCount > 0
                                ? `Depends on ${outgoingCount} initiative${outgoingCount === 1 ? '' : 's'}`
                                : `${reverseCount} initiative${reverseCount === 1 ? '' : 's'} depend on this`;

                          return (
                            <tr
                              key={initiative.id}
                              className="border-b border-gray-100 hover:bg-surface-hover transition-colors cursor-pointer group"
                              onClick={() => openViewInitiativeModal(initiative)}
                            >
                              {/* Initiative Name with Classification Pipe and Dependency Icon */}
                              <td className="py-2 px-4 pl-12 max-w-md">
                                <div className="flex items-center gap-3 min-w-0">
                                  {/* Classification Colored Pipe */}
                                  <div
                                    className="w-1 h-8 rounded-full flex-shrink-0"
                                    style={{
                                      backgroundColor: fc.isVisible('classification')
                                        ? (classification?.color || "#3B82F6")
                                        : "#D1D5DB",
                                    }}
                                  />
                                  {/* Star badge */}
                                  {initiative.isStarred && (
                                    <svg className="w-3.5 h-3.5 flex-shrink-0 text-amber-400" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                    </svg>
                                  )}
                                  {/* Title with tooltip (only when truncated). The row itself already opens
                                      the modal on click; the title additionally gets its own hover affordance
                                      (INITIATIVE_TITLE_LINK_CLASS) so it's consistent with Batch Update /
                                      Dependency Log / Scoring. */}
                                  <TruncatedTitle
                                    title={initiative.title}
                                    className={INITIATIVE_TITLE_LINK_CLASS}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openViewInitiativeModal(initiative);
                                    }}
                                  />
                                  {/* Dependency icon */}
                                  {labs.isEnabled('dependencies') && hasDependencies && (
                                    <div className="group/dep relative flex-shrink-0">
                                      <LinkIcon className="w-4 h-4 text-text-tertiary" />
                                      {/* Tooltip - positioned below */}
                                      <div className="absolute hidden group-hover/dep:block top-full left-0 mt-1 px-2 py-1 text-xs bg-gray-900 text-white rounded whitespace-nowrap z-50">
                                        {dependencyTooltip}
                                      </div>
                                    </div>
                                  )}
                                  {/* Comment count */}
                                  {((initiative as any)._count?.comments || 0) > 0 && (
                                    <div className="group/comment relative flex-shrink-0 flex items-center gap-0.5 text-text-tertiary">
                                      <ChatBubbleLeftIcon className="w-4 h-4" />
                                      <span className="text-xs">
                                        {(initiative as any)._count?.comments > 99 ? '99+' : (initiative as any)._count?.comments}
                                      </span>
                                      {/* Tooltip */}
                                      <div className="absolute hidden group-hover/comment:block top-full left-0 mt-1 px-2 py-1 text-xs bg-gray-900 text-white rounded whitespace-nowrap z-50">
                                        {(initiative as any)._count?.comments} comment{(initiative as any)._count?.comments !== 1 ? 's' : ''}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>

                              {/* Status */}
                              <td className="py-2 px-4 whitespace-nowrap">
                                {status && (
                                  <div className="flex items-center gap-2">
                                    <StatusDot color={status.color} />
                                    <span className="text-sm text-text-secondary">
                                      {status.name}
                                    </span>
                                  </div>
                                )}
                              </td>

                              {/* Start Quarter / Start Date */}
                              <td className="py-2 px-4 whitespace-nowrap">
                                <span className="text-sm text-text-secondary">
                                  {isWeekly || (initiative.datePrecision === 'day' && initiative.startDate)
                                    ? formatActualDate(initiative.startDate)
                                    : <MonthLabel value={getQuarterLabel(initiative.startQuarter ?? '')} />}
                                </span>
                              </td>

                              {/* End Quarter / End Date */}
                              {fc.isVisible('end') && (
                                <td className="py-2 px-4 whitespace-nowrap">
                                  <span className="text-sm text-text-secondary">
                                    {isWeekly || initiative.datePrecision === 'day'
                                      ? formatActualDate(initiative.endDate)
                                      : (initiative.endQuarter ? <MonthLabel value={getQuarterLabel(initiative.endQuarter)} /> : '—')}
                                  </span>
                                </td>
                              )}

                              {/* Owner */}
                              {fc.isVisible('owner') && (
                                <td className="py-2 px-4 whitespace-nowrap">
                                  <span className="text-sm text-text-secondary">
                                    {(initiative as any).user?.name || '—'}
                                  </span>
                                </td>
                              )}

                              {/* Tags */}
                              {fc.isVisible('tags') && (
                                <td className="py-2 px-4 whitespace-nowrap">
                                  <TagList tags={initiative.tags} />
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <ClassificationFooter />
    </div>
  );
}
